import React from 'react';
import { type TableProps } from 'antd';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { round } from 'lodash';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { ICndn, ICndnItem } from '@/interfaces/cndn';
import { ECndnReason } from '@/enums/ECndn';

interface IProps {
  cndn: Partial<ICndn>;
  showFooter?: boolean;
}

const CndnItemTable = ({ cndn, showFooter = true }: IProps) => {
  const columns: TableProps<Partial<ICndnItem>>['columns'] = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 130,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(
          round(Number(rec?.quantity) / Number(rec?.conversion), 2),
          2,
        ),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      dataIndex: 'quantity',
      align: 'right',
      className: 'text-blue-700 font-bold',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
    },
    {
      title: 'ราคา',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-green-700',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'รวมเงิน',
      width: 120,
      dataIndex: 'totalAmount',
      align: 'right',
      fixed: 'right',
      className: 'font-bold',
      render: (_: number) => {
        return formatNumber(_, 2);
      },
    },
  ];

  const {
    invoiceAmount,
    newInvoiceAmount,
    totalAmount,
    totalAfterDiscountAmount,
    discountAmount,
    vatAmount,
    grandTotalAmount,
    taxRate,
    reason,
  } = cndn;

  const items =
    reason == ECndnReason.RETURN_GOOD
      ? cndn?.creditDebitNoteItems?.filter(c => Number(c.quantity) > 0)
      : cndn?.creditDebitNoteItems?.filter(c => Number(c.unitPrice) > 0);

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<Partial<ICndnItem>>
        columns={columns}
        criteria={{}}
        data={{
          data: items || [],
          totalRecords: 0,
        }}
        loading={false}
        onTableChange={() => {
          console.log();
        }}
        scrollLength={1500}
        showPagination={false}
      />
      {showFooter && (
        <div className="flex justify-end">
          <div className="flex flex-col border border-red-400 rounded-lg p-2 text-bold w-[300px] bg-red-50">
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="มูลค่าตามใบแจ้งหนี้เดิม"
              detail={formatNumber(invoiceAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="มูลค่าที่ถูกต้อง"
              detail={formatNumber(newInvoiceAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="ผลต่าง"
              detail={formatNumber(totalAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="ส่วนลด (บาท)"
              detail={formatNumber(discountAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="ยอดรวมหลังหักส่วนลด"
              detail={formatNumber(totalAfterDiscountAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]    text-sm"
              label={`ภาษีมูลค่าเพิ่ม ${taxRate || ''} %`}
              detail={formatNumber(vatAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
            <DetailItem
              customMainClass="flex-row"
              customLabelClass="flex justify-end min-w-[140px]  text-sm"
              label="ยอดสุทธิ"
              detail={formatNumber(grandTotalAmount || 0, 2)}
              customClass="flex justify-end w-full text-black font-bold  text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CndnItemTable;
